// per docs/caiet-uc-spec.md §UC22-01 §UC22-02 §UC22-04
// The System Administrator's read over correction requests. Separate from
// api.ts because that one serves the Operator (UC51) and neither side should
// pick up the other's shapes.
import { apiClient } from '../../app/api-client';
import type { CorrectionRequest, CorrectionStatus } from './api';

export interface AdminCorrectionRow extends CorrectionRequest {
  medicineName: string;
  medicineForm: string;
  medicineDose: string;
  registrarName: string;
  // The location the request was made from, named: with the Registrar, the
  // "Raportor" (CF-63). '' when it was not recorded or cannot be named.
  siteName: string;
}

// One page of the register (docs/shape-ops-correction-admin-register.md §2 D1).
export interface AdminCorrectionPage {
  requests: AdminCorrectionRow[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// One (Registrar, location) pair the requests carry (D3); the site fields are ''
// for a request made before its location was recorded.
export interface AdminCorrectionFacet {
  registrarId: string;
  registrarName: string;
  siteId: string;
  siteName: string;
}

// The locations the «Locație» filter offers: the facets that name a location (the
// ones that do not are requests made before it was recorded), narrowed to one
// Registrar when one is chosen, and kept once per (Registrar, location). The
// facet list is not guaranteed unique, and two `<option>`s built from one pair
// made React log a duplicate-key error whenever the filter panel opened (V-034).
export function locationFacets(
  facets: readonly AdminCorrectionFacet[],
  registrarId: string,
): AdminCorrectionFacet[] {
  const seen = new Set<string>();
  const out: AdminCorrectionFacet[] = [];
  for (const facet of facets) {
    if (!facet.siteId) continue;
    if (registrarId && facet.registrarId !== registrarId) continue;
    const key = `${facet.registrarId}:${facet.siteId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(facet);
  }
  return out;
}

export type AdminCorrectionSort = 'newest' | 'oldest' | 'reporter' | 'medicine' | 'lot';

export interface CorrectionLotImpact {
  movementRows: number;
  siteCount: number;
  netQuantity: string;
  firstMovementDate: string;
  lastMovementDate: string;
  // The date of the newest confirmation, and every date the lot's rows carry.
  reportedExpiry: string;
  siteIds: string[];
  carriedExpiries: string[];
}

export interface MedicineBrief {
  code: string;
  name: string;
  pharmaceuticalForm: string;
  dose: string;
  dci: string;
  manufacturer: string;
  atcCode: string;
}

export interface RegistrarBrief {
  idno: string;
  name: string;
  kind: string;
}

export interface SiteBrief {
  id: string;
  name: string;
  internalCode: string;
}

// caiet UC22-04: one change of the lot's expiry, as the lot history keeps it.
// The precisions are DAY or MONTH; the approver is named by IDNP only (CF-63).
export interface LotHistoryEntry {
  previousExpiry: string;
  previousPrecision: string;
  newExpiry: string;
  newPrecision: string;
  correctionRequestId: string;
  changedByIdnp: string;
  changedAtIso: string;
}

export interface AdminCorrectionDetail {
  request: CorrectionRequest;
  impact: CorrectionLotImpact;
  medicine: MedicineBrief | null;
  registrar: RegistrarBrief | null;
  // The location the request was made from (CF-63: Registrar + location); null when the request
  // predates its location. `sites` are the locations holding the lot, secondary to it.
  requestSite: SiteBrief | null;
  sites: SiteBrief[];
  lotHistory: LotHistoryEntry[];
}

// caiet UC22-01: every criterion is the server's, so it holds over the whole
// register and not over one page (D1, D2).
export interface AdminCorrectionFilter {
  status?: string;
  registrarId?: string;
  siteId?: string;
  q?: string;
  from?: string;
  to?: string;
  sort?: AdminCorrectionSort;
  page?: number;
  pageSize?: number;
}

export async function listAdminCorrections(
  filter: AdminCorrectionFilter,
): Promise<AdminCorrectionPage> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value) query.set(key, String(value));
  }
  const suffix = query.toString();
  const { data } = await apiClient.get<AdminCorrectionPage>(
    `/corrections/admin${suffix ? `?${suffix}` : ''}`,
  );
  return {
    requests: data.requests ?? [],
    totalCount: data.totalCount ?? 0,
    page: data.page ?? 1,
    pageSize: data.pageSize ?? filter.pageSize ?? 0,
  };
}

// D4: every request counted by status, for the badge, the hub and the register.
export async function countAdminCorrections(): Promise<Record<CorrectionStatus, number>> {
  const { data } = await apiClient.get<{ counts: Record<CorrectionStatus, number> }>(
    '/corrections/admin/counts',
  );
  return data.counts;
}

// D3: the register's "Raportor" and "Locație" options.
export async function listAdminCorrectionFacets(): Promise<AdminCorrectionFacet[]> {
  const { data } = await apiClient.get<{ facets: AdminCorrectionFacet[] }>(
    '/corrections/admin/facets',
  );
  return data.facets ?? [];
}

// UC22-04 rewrites every row of the lot at the approved date, so an approval
// changes nothing only at a date every row already carries. The rows can carry
// two: an Operator asks at its own locations (CF-43) while another location
// reported the lot later under another date. core-service holds the same rule.
export function approvalChangesNothing(
  decidedExpiry: string,
  carriedExpiries: readonly string[],
): boolean {
  return carriedExpiries.length > 0 && carriedExpiries.every((expiry) => expiry === decidedExpiry);
}

export async function getAdminCorrection(id: string): Promise<AdminCorrectionDetail> {
  const { data } = await apiClient.get<AdminCorrectionDetail>(`/corrections/admin/${id}`);
  return data;
}

// The status is an internal machine value (caiet UC22-03 talks about a status,
// not about the word PENDING). Nothing renders it raw: every surface goes
// through these two, so the vocabulary the administrator reads is translated
// copy and the semantic colour is decided in one place.
export function statusLabelKey(status: CorrectionStatus | string): string {
  switch (status) {
    case 'PENDING':
      return 'corrections.status.pending';
    case 'APPROVED':
      return 'corrections.status.approved';
    case 'REJECTED':
      return 'corrections.status.rejected';
    default:
      return 'corrections.status.unknown';
  }
}

export function statusSemantic(status: CorrectionStatus | string): 'brand' | 'success' | 'danger' {
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'danger';
  return 'brand';
}
